import { useEffect, useRef } from "react";
import { isConnected } from "../../Upshow";
import { CONTENT_TYPE } from "../constants/event";
import { getActiveLiveContent } from "../utils/shared/events";

const RETRY_MS = 2000;
// The auto-open belongs to boot: past this window it must not fire on its own.
const MAX_WAIT_MS = 60000;

// Open the guide once after the device boots, unless a live event is on screen.
// index.js awaits the streaming readiness checks before rendering the EPG tree, so
// by the time this runs the checks have concluded.
//
// The one-shot is spent as soon as the guide opens by any means (user, deeplink) or
// `skip` turns true, and it is only retried, for at most MAX_WAIT_MS, while the device
// is offline or a dialog is up (`blocked`).
export default function useAutoOpenGuide({ epgReady, isOpen, skip, blocked, openEPG }) {
    const doneRef = useRef(false);
    const readySinceRef = useRef(null);
    const openEPGRef = useRef(openEPG);
    openEPGRef.current = openEPG;

    useEffect(() => {
        // Any open counts, even before the data is ready: closing it afterwards must not re-open it.
        if (isOpen) doneRef.current = true;
        if (doneRef.current || !epgReady) return;

        // `skip` is latched because its inputs (e.g. the deeplink id) are cleared when the guide closes.
        if (skip) {
            doneRef.current = true;
            return;
        }

        if (readySinceRef.current === null) readySinceRef.current = Date.now();

        const finish = () => {
            doneRef.current = true;
            return true;
        };

        // Returns true once there is nothing left to try.
        const attempt = () => {
            if (Date.now() - readySinceRef.current > MAX_WAIT_MS) return finish();

            // Read the cached state directly: the context's activeLiveContent can lag the events fetch.
            if (getActiveLiveContent()?.contentType === CONTENT_TYPE.LIVE_EVENT) return finish();

            // openEPG is a silent no-op while disconnected; same guards as the manual open.
            if (blocked || !isConnected()) return false;

            openEPGRef.current();
            return finish();
        };

        if (attempt()) return;
        const id = setInterval(() => { if (attempt()) clearInterval(id); }, RETRY_MS);
        return () => clearInterval(id);
    }, [epgReady, isOpen, skip, blocked]);
}
